import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { BuscaRemota, Rotulo, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, post } from '../../nucleo/api';
import { nomePessoa } from '../../nucleo/formato';
import type { Ficha, TipoVinculo } from './tipos';

const TIPOS: TipoVinculo[] = ['socio_de', 'representante_de', 'responsavel_por'];

/**
 * Fase 2 (W5, L7): vínculos entre pessoas e empresas (sócio de, representante de, responsável por), nos dois sentidos. Para o
 * escritório contábil, sócio de várias empresas é comum. Nome do outro cadastro pela regra de nomes do modo discreto.
 */
export function CartaoVinculos({ p, podeEditar, recarregar }: { p: Ficha; podeEditar: boolean; recarregar: () => Promise<unknown> }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [novo, setNovo] = useState(false);
  const [tipo, setTipo] = useState<TipoVinculo>('socio_de');
  const [outro, setOutro] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => post(`/cadastros/parceiros/${p.id}/vinculos-pessoas`, { para_id: outro, tipo }),
    onSuccess: async () => {
      await recarregar();
      setNovo(false);
      setOutro(null);
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/cadastros/parceiros/${p.id}/vinculos-pessoas/${id}`),
    onSuccess: () => recarregar(),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <Cartao
      titulo={t('ficha.vinculosPessoas')}
      acao={
        podeEditar && (
          <Botao
            variante="fantasma"
            tamanho="sm"
            icone={<Plus className="size-4" />}
            onClick={() => {
              setErro(null);
              setNovo(true);
            }}
          >
            {t('acoes.adicionarVinculo')}
          </Botao>
        )
      }
    >
      {p.vinculos_pessoas.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('ficha.vinculosVazio')}</p>
      ) : (
        <ul className="divide-y divide-borda">
          {p.vinculos_pessoas.map((v) => (
            <li key={v.id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
              <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                <span className="text-tinta-3">{t(`valores.vinculo.${v.tipo}.${v.direcao}`)}</span>
                <Link to={`/cadastros/cadastros/parceiro/${v.outro_id}`} className="min-w-0 font-medium break-words hover:text-acento-forte">
                  {nomePessoa(v.outro_nome)}
                </Link>
                {v.outro_arquivado && <Selo cor="perigo">{t('ficha.arquivado')}</Selo>}
              </span>
              {podeEditar && (
                <button type="button" aria-label={`${tn('comum.excluir')} ${t(`valores.vinculo.${v.tipo}.${v.direcao}`)}`} onClick={() => remover.mutate(v.id)} className="shrink-0 rounded p-1 text-tinta-3 hover:text-perigo">
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Modal
        aberto={novo}
        aoFechar={() => setNovo(false)}
        titulo={t('acoes.adicionarVinculo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setNovo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!outro} carregando={criar.isPending} onClick={() => criar.mutate()}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
              {erro}
            </p>
          )}
          <Rotulo id="vinculo-tipo" rotulo={t('campos.vinculoTipo')}>
            <Selecao id="vinculo-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoVinculo)} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: t(`valores.vinculoOpcao.${x}`) }))} />
          </Rotulo>
          <Rotulo id="vinculo-outro" rotulo={t('campos.vinculoOutro')}>
            <BuscaRemota id="vinculo-outro" def={{ caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome) }} valor={outro} aoMudar={(v) => setOutro(v === p.id ? null : v)} />
          </Rotulo>
        </div>
      </Modal>
    </Cartao>
  );
}
